import {
	ClientPropertyBuildingDispositionEnum,
	ClientPropertyBuildingMaterialEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { toBooleanGroup } from "web_myplann/Portfolio/Helpers/booleanGroups";
import { BUILDING_OPTIONAL_PARAMS } from "web_myplann/Portfolio/Helpers/buildingOptionalParams";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalNonNegativeInteger } from "web_myplann/Shared/Validation/optionalNonNegativeInteger";
import { optionalZipCode } from "web_myplann/Shared/Validation/optionalZipCode";
import { refineRequiredMinimum } from "web_myplann/Shared/Validation/refineRequiredMinimum";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const MAX_FLOORS = 999;

export const buildingMinimumGroup = [
	"location",
	"locationMunicipality",
	"locationZipCode",
	"description",
] as const;

export const getBuildingFormSchema = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddRealEstate.Validations.maxValue);
	const integerMessages = {
		integerMessage: t(
			Resources.Portfolio.AddRealEstate.Validations.wholeNumber,
		),
		maxMessage,
	};
	const maxLength = (max: number) =>
		t(Resources.Portfolio.AddRealEstate.Validations.maxLength, { max });

	return z
		.object({
			name: trimmedNullable(
				z
					.string()
					.max(
						ValidationLimits.TextLength.LongName,
						maxLength(ValidationLimits.TextLength.LongName),
					),
			),
			location: trimmedNullable(
				z
					.string()
					.max(
						ValidationLimits.TextLength.Location,
						maxLength(ValidationLimits.TextLength.Location),
					),
			),
			locationMunicipality: trimmedNullable(
				z
					.string()
					.max(
						ValidationLimits.TextLength.Short,
						maxLength(ValidationLimits.TextLength.Short),
					),
			),
			locationZipCode: optionalZipCode(
				t(Resources.Portfolio.AddRealEstate.Validations.zipFormat),
			),
			usableArea: optionalNonNegativeInteger(
				ValidationLimits.Amount.Max,
				integerMessages,
			),
			floorArea: optionalNonNegativeInteger(
				ValidationLimits.Amount.Max,
				integerMessages,
			),
			householdArea: optionalNonNegativeInteger(
				ValidationLimits.Amount.Max,
				integerMessages,
			),
			landSize: optionalNonNegativeInteger(
				ValidationLimits.Amount.Max,
				integerMessages,
			),
			disposition: z
				.nativeEnum(ClientPropertyBuildingDispositionEnum)
				.nullable(),
			buildingMaterial: z
				.nativeEnum(ClientPropertyBuildingMaterialEnum)
				.nullable(),
			numberOfFloors: optionalNonNegativeInteger(MAX_FLOORS, integerMessages),
			amenities: z.array(z.string()),
			marketValue: optionalNonNegativeInteger(
				ValidationLimits.Amount.Max,
				integerMessages,
			),
			reproductionValue: optionalNonNegativeInteger(
				ValidationLimits.Amount.Max,
				integerMessages,
			),
			interiorValue: optionalNonNegativeInteger(
				ValidationLimits.Amount.Max,
				integerMessages,
			),
			purchaseDate: optionalDateAsString(DateFormat.Iso),
			description: trimmedNullable(
				z
					.string()
					.max(
						ValidationLimits.TextLength.Note,
						maxLength(ValidationLimits.TextLength.Note),
					),
			),
			forInvestments: z.boolean(),
		})
		.superRefine(refineRequiredMinimum(buildingMinimumGroup))
		.transform(({ amenities, ...rest }) => ({
			...rest,
			...toBooleanGroup(BUILDING_OPTIONAL_PARAMS, amenities),
		}));
};

export type BuildingFormInput = z.input<
	ReturnType<typeof getBuildingFormSchema>
>;
export type BuildingFormOutput = z.output<
	ReturnType<typeof getBuildingFormSchema>
>;
